import { Model } from 'app/model/backendmodel';
import { Http, Response } from 'app/services/http';
import { IDataSourceCredentialUrlResponse, IAzureResource, ISOCDataLakeAnalyticsAccount } from '../../interfaces/IAzureResource';
import { AuthService } from '../authentication/auth.service';
import { AuthConstants } from '../authentication/auth-constants';
import { FactoryUrlInformationService } from '../factory-url-information.service';
import { LoggerService } from 'app/logger/logger.service';
import { CrudEvent } from 'app/logger/logger.crud-event';
import * as Constants from '../globals';
import * as HttpUtil from 'app/shared/utils/http.utils';
import { convertObjectToString } from '../../shared/utils/string.util';
import { BaseMetadataClient } from '../base-metadata-client';
import { notImplementedFunction } from 'app/extension/api/common';
import { retryWhen } from 'rxjs/operators';
import { LoggerHelper } from '../../logger/logger-helper';
import { Inject } from '@angular/core';
import { IResourceContext, RESOURCE_CONTEXT_DATA } from 'app/multi-contexts/resource-context.typings';

export class AdfMetadataClient extends BaseMetadataClient {
    public className = 'AdfMetadataClient';
    protected apiVersion: string = Constants.GlobalVariables.dataFactoryVersion;

    public constructor(
        @Inject(RESOURCE_CONTEXT_DATA) _contextData: IResourceContext,
        readonly httpClient: Http,
        readonly authService: AuthService,
        readonly urlService: FactoryUrlInformationService,
        readonly logger: LoggerService,
        readonly loggerHelper: LoggerHelper,
    ) {
        super(_contextData, httpClient, authService, urlService, logger, loggerHelper);
    }

    public async getResource(resourceType?: Constants.ResourceType, resourceName?: string, omitErrorLog?: boolean): Promise<IAzureResource> {
        if (!resourceType && !resourceName) {
            if (this.urlService.dataFactoryName) {
                return super.getResource();
            }
            return null;
        } else {
            return super.getResource(resourceType, resourceName, omitErrorLog);
        }
    }

    public async update(resourceType: Constants.ResourceType, name: string, payload: {}): Promise<IAzureResource> {
        if (resourceType === 'factory') {
            const factory = this._setVstsConfiguration(payload);
            return (await super.update('factory', null, factory)) as IAzureResource;
        } else {
            return super.update(resourceType, name, payload);
        }
    }
    public async rename(resourceType: Constants.ResourceType, name: string, payload: {}): Promise<Response> {
        return notImplementedFunction();
    }

    public async getDataSourceCredentialUrl(lsName: string, sessionId: string): Promise<IDataSourceCredentialUrlResponse> {
        return new Promise<IDataSourceCredentialUrlResponse>(async (resolve, reject) => {
            const headers = await this.getDefaultHeadersAsync();
            if (sessionId) {
                headers.append('x-ms-client-request-id', sessionId);
            }
            const url = `${this.getBaseUri()}/getsourcecredentialsurl?linkedServiceName=${lsName}`;
            this.httpClient.get(url, { headers, params: this.getDefaultParams() })
                .pipe(retryWhen(HttpUtil.defaultRetryPolicy))
                .toPromise()
                .then(async (resp) => {
                    resolve(resp.json());
                })
                .catch((reason) => {
                    this.logger.error(this.className, `getDataSourceCredentialUrl: Error while calling url. Error: ${convertObjectToString(reason)}`, { getDataSourceCredentialUrl: url });
                    reject(reason);
                });
        });
    }

    public getSparkHistoryEndpoint(basUri: string): Promise<string> {
        return notImplementedFunction();
    }

    public getSparkHistoryServerVersion(workspace: Model.Workspace): Promise<string> {
        return notImplementedFunction();
    }

    public getSOCDataLakeAnalyticsAccount(workspace: Model.Workspace): Promise<ISOCDataLakeAnalyticsAccount> {
        return notImplementedFunction();
    }

    public async upgradeLinkedService(linkedServiceName: string): Promise<Response> {
        const fabricScope = `${AuthConstants.FABRIC_RESOURCE_ID}`;
        const accessToken = await this.authService.getToken(fabricScope);
        const headers = await this.getDefaultHeadersAsync();
        const url = this.createSingleResourceUri('linkedService', linkedServiceName) + '/upgrade';
        const displayName = `${this.urlService.dataFactoryName}_${linkedServiceName}`;

        return new Promise((resolve, reject) => {
            this.httpClient
                .post(url, JSON.stringify({ displayName, accessToken }), {
                    headers,
                    params: this.getDefaultParams(null, this.apiVersion)
                })
                .pipe(retryWhen(HttpUtil.defaultRetryPolicy))
                .toPromise()
                .then((resp) => {
                    this.logger.logEvent(
                        new CrudEvent('upgradeLinkedService', linkedServiceName, 'linkedService', '', undefined, resp.status.toString())
                    );
                    resolve(resp);
                })
                .catch((reason) => {
                    this.loggerHelper.logRequestError(
                        this.className,
                        'upgradeLinkedService: Error while calling url',
                        reason,
                        { upgradeLinkedServiceUrl: url }
                    );
                    reject(reason);
                });
        });
    }

    private _setVstsConfiguration(factory: Model.Factory): Model.Factory {
        // set vstsConfiguraiton according to repoConfiguration for preview Version API
        // There are two cases: removeGitRepoInformation and configure vsts repo
        if (factory.properties && (!factory.properties.repoConfiguration || factory.properties.repoConfiguration.type === 'FactoryVSTSConfiguration')) {
            factory.properties.vstsConfiguration = factory.properties.repoConfiguration;
        }
        return factory;
    }
}
